import { useEffect, useMemo, useState } from "react";
import type { Projeto } from "@/types/ui-models";

interface ProjectFiltersProps {
  /** Projetos reais carregados: as opcoes dos filtros saem deles. */
  projetos: Projeto[];
  onFilter: (filters: Record<string, string>) => void;
}

function statusLabel(s: string) {
  const t = s.replace(/_/g, " ");
  return t.charAt(0).toUpperCase() + t.slice(1);
}

function distinct(values: string[]) {
  return Array.from(new Set(values.filter(Boolean))).sort((a, b) => a.localeCompare(b, "pt-BR"));
}

export default function ProjectFilters({ projetos, onFilter }: ProjectFiltersProps) {
  const [search, setSearch] = useState("");
  const [municipio, setMunicipio] = useState("");
  const [uf, setUf] = useState("");
  const [tipo, setTipo] = useState("");
  const [status, setStatus] = useState("");
  const [showFilters, setShowFilters] = useState(false);

  const ufs = useMemo(() => distinct(projetos.map((p) => p.uf)), [projetos]);
  const tipos = useMemo(() => distinct(projetos.map((p) => p.tipo)), [projetos]);
  const statuses = useMemo(() => distinct(projetos.map((p) => p.status)), [projetos]);

  useEffect(() => {
    onFilter({
      search: search.toLowerCase(),
      municipio: municipio.toLowerCase(),
      uf,
      tipo,
      status,
    });
  }, [search, municipio, uf, tipo, status, onFilter]);

  const clearFilters = () => {
    setSearch("");
    setMunicipio("");
    setUf("");
    setTipo("");
    setStatus("");
  };

  const activeCount = [search, municipio, uf, tipo, status].filter(Boolean).length;

  return (
    <div className="bg-white rounded-card border border-background-200 p-4">
      {/* Search + Toggle */}
      <div className="flex items-center gap-2">
        <div className="flex-1 relative">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
          <input
            type="text"
            placeholder="Buscar por nome do projeto..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 text-sm border border-background-200 rounded-btn focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 bg-background-50"
          />
        </div>
        <button
          onClick={() => setShowFilters(!showFilters)}
          className={`btn-outline text-xs px-3 py-2 cursor-pointer ${showFilters ? "border-primary-400 text-primary-600 bg-primary-50" : ""}`}
        >
          <i className="ri-filter-3-line"></i>
          Filtros
          {activeCount > 0 && (
            <span className="ml-1 w-4 h-4 rounded-full bg-primary-500 text-white text-[10px] flex items-center justify-center">
              {activeCount}
            </span>
          )}
        </button>
        {activeCount > 0 && (
          <button onClick={clearFilters} className="btn-outline text-xs px-3 py-2 cursor-pointer">
            <i className="ri-close-line"></i>
            Limpar
          </button>
        )}
      </div>

      {/* Expanded Filters */}
      {showFilters && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-3 pt-3 border-t border-background-200">
          <div>
            <label className="text-xs text-foreground-500 mb-1 block">Município</label>
            <input
              type="text"
              placeholder="Nome do município"
              value={municipio}
              onChange={(e) => setMunicipio(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-background-200 rounded-btn focus:outline-none focus:border-primary-400 bg-background-50"
            />
          </div>
          <div>
            <label className="text-xs text-foreground-500 mb-1 block">UF</label>
            <select
              value={uf}
              onChange={(e) => setUf(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-background-200 rounded-btn focus:outline-none focus:border-primary-400 bg-background-50"
            >
              <option value="">Todas</option>
              {ufs.map((u) => (
                <option key={u} value={u}>{u}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs text-foreground-500 mb-1 block">Tipo de Projeto</label>
            <select
              value={tipo}
              onChange={(e) => setTipo(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-background-200 rounded-btn focus:outline-none focus:border-primary-400 bg-background-50"
            >
              <option value="">Todos</option>
              {tipos.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs text-foreground-500 mb-1 block">Status</label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-background-200 rounded-btn focus:outline-none focus:border-primary-400 bg-background-50"
            >
              <option value="">Todos</option>
              {statuses.map((s) => (
                <option key={s} value={s}>{statusLabel(s)}</option>
              ))}
            </select>
          </div>
        </div>
      )}
    </div>
  );
}
